import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { HISTORICAL_SCENARIO, selectors } from 'state';

import MarketsTabV4 from '../MarketsTabV4';
import MarketBoard from '../MarketsTabV4/MarketBoard';
import { MARKETS_ASSESSMENT_PREVIEW_ENABLED } from '../MarketsTabV4/assessmentMode';
import PortfolioValueEvolution from '../MarketsTab/PortfolioOverview/PortfolioValueEvolution';
import './marketsTabV5.css';
import { isV5MarketsExperience } from './route';
import SessionProgress from './SessionProgress';

export { isV5MarketsExperience, isV5MarketsLocation } from './route';

/**
 * V5 changes only the large-screen canvas boundary. All market behavior stays
 * on the maintained V4 board, while earlier session-selected views keep
 * routing through the untouched V4 entrypoint.
 */
const MarketsTabV5: React.FC = () => {
  const session = useSelector(selectors.activeSession);
  const isV5Active = isV5MarketsExperience(session?.which_markets);

  useEffect(() => {
    if (!isV5Active) return undefined;

    document.body.classList.add('markets-v5-is-active');

    return () => {
      document.body.classList.remove('markets-v5-is-active');
    };
  }, [isV5Active]);

  if (!isV5Active) return <MarketsTabV4 />;

  return (
    <div className="markets-v5-shell">
      <div
        className="markets-v5-canvas"
        role="region"
        aria-label="Markets V5 centered canvas"
      >
        <MarketBoard
          assessmentPreviewEnabled={MARKETS_ASSESSMENT_PREVIEW_ENABLED}
          animateHistoricalPriceUpdates
          fitInstrumentMarketValues
          showBondCountryMaturityLabel
          showBondDv01Summary
          hideInstrumentTicker
          showAvgOpenPrice
          showMarketClosedLabel
          hiddenUnderlyingAssetIds={session.markets_v5_hidden_assets ?? []}
          heroTotalSessionDays={
            session.scenario_type === HISTORICAL_SCENARIO
              ? session.total_simulated_days + 1
              : undefined
          }
          heroProgress={
            session.scenario_type === HISTORICAL_SCENARIO ? (
              <SessionProgress
                remainingSimulatedDays={session.remaining_simulated_days}
                totalSimulatedDays={session.total_simulated_days}
              />
            ) : undefined
          }
          portfolioEvolution={
            <section
              className="markets-v4-price-graph markets-v5-portfolio-evolution"
              aria-label="Portfolio return evolution chart"
            >
              <PortfolioValueEvolution
                height="100%"
                style={{ height: '100%', minWidth: 0 }}
              />
            </section>
          }
        />
      </div>
    </div>
  );
};

export default MarketsTabV5;
